import { Spin } from "antd";
import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

import { MainTemplate } from "./MainTemaplete";
import { TopTemaplete } from "./TopTemaplete";
import { UserTemplate } from "./UserTemplate";
import { OtherUserTemplate } from "./OhterUser/OtherUserTemplate";
import { ModalPost } from "../../components/modals/ModalPost";
import { UiTextsContext } from "../../contexts/Contexts";
import {
  basePath,
  courtesyPath,
  forumPath,
  homePath,
  loginPath,
  mapPath,
  passportPath,
  postWasNotFoundPath,
  publicationDetailPath,
  publicationGalleryPath,
  recoverPassPath,
  singUpPath,
  userConnectionsPath,
  userCourtesyPath,
  userMemoriesPath,
  userPublicationsPath,
  userSavedPath,
  userVisitPath,
  userPassportPath,
  userPath,
  userSetupPath,
  verifyEmailPath,
  ratingPath,
  settingsPrivacyBlockPath,
  settingsPrivacyPath,
  settingsPrivacyPrivacyPath,
  hashtagPath,
} from "../../routes/routes";

const publicEventsBasePath = "/public/events";

const LAYOUT = Object.freeze({
  BASE: "base",
  MAIN: "main",
  USER: "user",
  OTHER_USER: "otherUser",
  TOP: "top",
});

const baseTemplateArray = [
  basePath,
  loginPath,
  singUpPath,
  recoverPassPath,
  verifyEmailPath,
  passportPath,
  userSetupPath,
  userPublicationsPath,
  userMemoriesPath,
  userVisitPath,
  userSavedPath,
  userConnectionsPath,
  userCourtesyPath,
  settingsPrivacyPath,
  settingsPrivacyPrivacyPath,
  settingsPrivacyBlockPath,
  publicEventsBasePath,
];

const mainTemplateArray = [
  homePath,
  publicationDetailPath,
  publicationGalleryPath,
  postWasNotFoundPath,
  hashtagPath,
];

const userTemplateArray = [];

const otherUserTemplateArray = [userPath];

const topTemplateArray = [
  userPassportPath,
  courtesyPath,
  forumPath,
  mapPath,
  ratingPath,
];

const matchesAnyRoute = (currentPath, routes) =>
  routes.some((route) => currentPath.startsWith(route));

/**
 * Resuelve el layout de manera sincrónica antes de renderizar la página.
 *
 * El orden conserva la prioridad que tenía el setLayout anterior:
 * TOP > OTHER_USER > USER > MAIN > BASE.
 *
 * De esta forma evitamos renderizar primero `routes` con el layout limpio
 * y volver a montar la misma pantalla después de ejecutar un useEffect.
 */
const resolveLayout = (currentPath) => {
  if (matchesAnyRoute(currentPath, topTemplateArray)) {
    return LAYOUT.TOP;
  }

  if (matchesAnyRoute(currentPath, otherUserTemplateArray)) {
    return LAYOUT.OTHER_USER;
  }

  if (matchesAnyRoute(currentPath, userTemplateArray)) {
    return LAYOUT.USER;
  }

  if (matchesAnyRoute(currentPath, mainTemplateArray)) {
    return LAYOUT.MAIN;
  }

  if (matchesAnyRoute(currentPath, baseTemplateArray) || currentPath === "") {
    return LAYOUT.BASE;
  }

  // Se conserva un fallback seguro para rutas que no estén clasificadas.
  return LAYOUT.BASE;
};

export const TemplateSelector = ({ routes }) => {
  const { texts } = useContext(UiTextsContext);
  const location = useLocation();
  const isMobile = useMediaQuery({ query: "(max-width: 1200px)" });
  const [showModalPost, setShowModalPost] = useState(false);

  // Con HashRouter, location.pathname ya es la ruta que va después del "#".
  const currentPath = location.pathname;
  // VIVA-985: el enlace "Ir al inicio" del sidebar venia encendido solo en la
  // galeria de publicaciones. En el detalle de publicacion y en los resultados de
  // hashtag el sidebar arranca pegado al tope y su avatar queda encima del logo de
  // la barra superior —medido con elementFromPoint: en el centro de ese logo se
  // pinta `vn-avatar`—, asi que esas dos pantallas se quedaban sin ningun acceso a
  // inicio. Encender el brand del sidebar lo devuelve, superpuesto al del navbar.
  //
  // El home queda fuera a proposito: ahi el logo de la barra superior si se ve
  // (elementFromPoint devuelve el propio logo), y encender el brand pondria dos
  // logos apilados en la pantalla mas visitada. Decision registrada en el ticket de
  // contexto del proyecto.
  const showMainSidebarLogo =
    currentPath.startsWith(publicationGalleryPath) ||
    currentPath.startsWith(publicationDetailPath) ||
    currentPath.startsWith(hashtagPath);
  const layout = resolveLayout(currentPath);

  const renderLayout = () => {
    switch (layout) {
      case LAYOUT.MAIN:
        return (
          <MainTemplate
            routes={routes}
            isMobile={isMobile}
            uiTexts={texts}
            showSidebarLogo={showMainSidebarLogo}
          />
        );

      case LAYOUT.USER:
        return (
          <UserTemplate
            routes={routes}
            setShowModalPost={setShowModalPost}
            isMobile={isMobile}
            uiTexts={texts}
          />
        );

      case LAYOUT.OTHER_USER:
        return (
          <OtherUserTemplate
            routes={routes}
            setShowModalPost={setShowModalPost}
            isMobile={isMobile}
            uiTexts={texts}
          />
        );

      case LAYOUT.TOP:
        return <TopTemaplete routes={routes} />;

      case LAYOUT.BASE:
      default:
        return <>{routes}</>;
    }
  };

  return (
    <Spin
      spinning={false}
      indicator={
        <LoadingOutlined
          style={{ fontSize: 48, color: "var(--main-viva-color)" }}
          spin
        />
      }
    >
      {renderLayout()}

      {/*
        ((layout === LAYOUT.MAIN || layout === LAYOUT.USER) &&
          !currentPath.includes("/chat")) ? (
          <FloatButton
            icon={<PlusOutlined />}
            onClick={() => setShowModalPost(true)}
            className="float-publish-button"
          />
        ) : null
      */}

      <ModalPost open={showModalPost} setOpen={setShowModalPost} />
    </Spin>
  );
};
